import { useEffect } from 'react'

export const STICKY_HEADER_CLASS = 'body__sticky-header'
export const STICKY_HEADER_SCROLLED_CLASS = 'body__sticky-header--scrolled'
export const STICKY_HEADER_OFFSET_PROP = '--app-sticky-header-offset'

/**
 * Always-sticky app header, applied app-wide from `HeaderApp` (the `#header-app`
 * mount). Adds `body__sticky-header` while `enabled`, which pins `#header-app`
 * (see `_HeaderApp.scss` for why the pin lives there and not on
 * `.headerV2__top`). A rAF-throttled scroll listener toggles the `--scrolled`
 * variant so the transparent header states (the hero overlays: dark-mode
 * ≥768px, member-home first-touch) keep their designed look at page top and go
 * opaque once content scrolls underneath them.
 *
 * It also MEASURES the pinned header block and publishes the height as
 * `--app-sticky-header-offset` inline on <body> — the one source of truth
 * every other sticky surface consumes (`community-my-library__sidebar`,
 * `playlist-tab-container__sticky-controls`, and the circles drawer's
 * `--app-drawer-top` / `--app-drawer-top-full`). Measuring the DOM (kept live
 * via ResizeObserver, with a window-resize fallback) means banners/alert bars
 * and future header changes are absorbed automatically instead of trusting a
 * 60px constant. The measured height is also published as `scroll-padding-top`
 * on the root element so keyboard-focus and anchor scrolls land below the
 * pinned header instead of underneath it (WCAG 2.4.11 Focus Not Obscured).
 * (`offsetHeight` excludes margins, so dark-mode's `margin-bottom: -60px`
 * hero-overlay trick doesn't skew it.)
 *
 * `enabled` is the header's own render gate (`HeaderApp` passes whether the
 * header is on the page) — off ⇒ no classes, no offset property ⇒ the stock
 * scroll-away header and every consumer's fallback value.
 *
 * @param enabled Whether the app header is rendered (pin only when it exists).
 */
export default function useStickyHeader (enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return undefined
    const { body } = document
    body.classList.add(STICKY_HEADER_CLASS)

    const updateScrolled = () => {
      body.classList.toggle(STICKY_HEADER_SCROLLED_CLASS, window.scrollY > 0)
    }

    // Publish the measured header-block height. Missing #header-app (legacy
    // header pages, tests) ⇒ no write, consumers fall back to their defaults.
    // The last written value is cached so a no-op re-measure skips the <body>
    // write — every var() consumer restyles when the property changes.
    const headerApp = document.getElementById('header-app')
    let lastOffset = ''
    const updateOffset = () => {
      if (!headerApp) return
      const offset = `${headerApp.offsetHeight}px`
      if (offset === lastOffset) return
      lastOffset = offset
      body.style.setProperty(STICKY_HEADER_OFFSET_PROP, offset)
      // Scroll-into-view (keyboard focus, anchor jumps) lands below the pinned
      // header instead of underneath it.
      document.documentElement.style.setProperty('scroll-padding-top', offset)
    }

    // Throttle scroll work to one class write per animation frame.
    let scrollRafId = 0
    const onScroll = () => {
      if (scrollRafId) return
      scrollRafId = requestAnimationFrame(() => {
        scrollRafId = 0
        updateScrolled()
      })
    }
    // The resize fallback shares the same rAF discipline: mobile browsers fire
    // resize continuously while the URL bar collapses, and each unthrottled
    // pass would force layout (offsetHeight) + a document-wide var() recalc.
    let resizeRafId = 0
    const onResize = () => {
      if (resizeRafId) return
      resizeRafId = requestAnimationFrame(() => {
        resizeRafId = 0
        updateOffset()
      })
    }

    // Set once up front so mounting on an already-scrolled page is correct.
    updateScrolled()
    updateOffset()
    window.addEventListener('scroll', onScroll, { passive: true })
    // ResizeObserver covers ALL header-block height changes — window resizes
    // included, plus alert bar / app-download banner toggles — and its
    // callbacks already run after layout, so it needs no throttle. The window
    // resize listener is only the fallback where it's unavailable (jsdom).
    let observer: ResizeObserver | null = null
    if (headerApp && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(updateOffset)
      observer.observe(headerApp)
    } else {
      window.addEventListener('resize', onResize)
    }

    return () => {
      if (scrollRafId) cancelAnimationFrame(scrollRafId)
      if (resizeRafId) cancelAnimationFrame(resizeRafId)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
      observer?.disconnect()
      document.documentElement.style.removeProperty('scroll-padding-top')
      body.style.removeProperty(STICKY_HEADER_OFFSET_PROP)
      body.classList.remove(STICKY_HEADER_CLASS, STICKY_HEADER_SCROLLED_CLASS)
    }
  }, [enabled])
}
